import React, { useState, useEffect } from 'react';
import { api } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import StatusChip from '../components/shared/StatusChip';
import { FileBarChart, Plus, ArrowRight, Check, MessageSquare, RefreshCw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ComparisonDashboard() {
  const [comparisons, setComparisons] = useState([]);
  const [encounters, setEncounters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedEncounter, setSelectedEncounter] = useState(null);
  const [actualInput, setActualInput] = useState({ primary_code: '', diagnosis_codes: '' });
  const [verdictInput, setVerdictInput] = useState({ verdict: '', notes: '' });

  const fetchData = async () => {
    try {
      const [compRes] = await Promise.all([
        api.get('/comparisons'),
      ]);
      setComparisons(compRes.data.comparisons || []);
    } catch (err) {
      toast.error('Failed to load comparisons');
    } finally {
      setLoading(false);
    }
  };

  const fetchEncounters = async () => {
    try {
      const res = await api.get('/appointments/today');
      const completedAppts = (res.data.appointments || []).filter(a => a.status === 'completed' && a.encounter_id);
      setEncounters(completedAppts);
    } catch (err) { /* silent */ }
  };

  useEffect(() => { fetchData(); fetchEncounters(); }, []);

  const createComparison = async (appt) => {
    try {
      await api.post('/comparisons', { encounter_id: appt.encounter_id, patient_id: appt.patient_id });
      toast.success('Comparison created');
      setShowCreate(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to create comparison');
    }
  };

  const submitActual = async (compId) => {
    try {
      await api.put(`/comparisons/${compId}/actual`, {
        primary_code: actualInput.primary_code,
        diagnosis_codes: actualInput.diagnosis_codes.split(',').map(s => s.trim()).filter(Boolean),
      });
      toast.success('Actual billing entered');
      setActualInput({ primary_code: '', diagnosis_codes: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  const submitVerdict = async (compId) => {
    try {
      await api.put(`/comparisons/${compId}/verdict`, verdictInput);
      toast.success('Verdict recorded');
      setVerdictInput({ verdict: '', notes: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed');
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl animate-fadeIn">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>Comparison Dashboard</h1>
          <p className="text-sm text-muted-foreground mt-1">AI suggestions vs. actual billing codes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchData} className="gap-1">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
          <Button size="sm" onClick={() => { setShowCreate(!showCreate); fetchEncounters(); }} className="gap-1">
            <Plus className="w-3.5 h-3.5" /> New Comparison
          </Button>
        </div>
      </div>

      {/* Create Panel */}
      {showCreate && (
        <Card className="mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Create Comparison from Finalized Encounter</CardTitle>
          </CardHeader>
          <CardContent>
            {encounters.filter(e => e.encounter_id).length === 0 ? (
              <p className="text-xs text-muted-foreground">No completed encounters available for comparison</p>
            ) : (
              <div className="space-y-2">
                {encounters.filter(e => e.encounter_id).map(appt => (
                  <div key={appt.id} className="flex items-center justify-between py-2 border-b">
                    <div>
                      <p className="text-sm">{appt.patient_name}</p>
                      <p className="text-xs text-muted-foreground">{appt.time} • {appt.visit_type}</p>
                    </div>
                    <Button size="sm" variant="outline" className="text-xs" onClick={() => createComparison(appt)}>
                      Create
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Comparison List */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : comparisons.length === 0 ? (
          <div className="text-center py-16">
            <FileBarChart className="w-10 h-10 mx-auto mb-3 text-muted-foreground/30" />
            <p className="text-sm text-muted-foreground">No comparisons yet</p>
            <p className="text-xs text-muted-foreground mt-1">Create one after an encounter is finalized</p>
          </div>
        ) : (
          comparisons.map(comp => (
            <Card key={comp.id} className="card-hover">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <FileBarChart className="w-4 h-4 text-primary" />
                    <span className="text-xs text-muted-foreground font-mono">{comp.encounter_id?.slice(0, 8)}</span>
                    <StatusChip status={comp.status} size="xs" />
                  </div>
                  {comp.verdict && (
                    <Badge variant="outline" className={`text-xs ${
                      comp.verdict === 'agree' ? 'bg-teal-50 text-teal-700' :
                      comp.verdict === 'disagree' ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-700'
                    }`}>
                      {comp.verdict}
                    </Badge>
                  )}
                </div>

                {/* Side by side */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="comparison-dashboard-ai-column">
                  {/* AI Output */}
                  <div className="bg-secondary/50 rounded-lg p-3">
                    <p className="text-[10px] font-semibold uppercase text-muted-foreground mb-2">AI Suggested</p>
                    {comp.ai_output ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">E/M:</span>
                          <Badge className="font-mono text-xs">{comp.ai_output.primary_code || 'N/A'}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs text-muted-foreground">Dx:</span>
                          {(comp.ai_output.diagnosis_codes || []).map((dx, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] font-mono">{dx}</Badge>
                          ))}
                        </div>
                      </div>
                    ) : <p className="text-xs text-muted-foreground">No AI output</p>}
                  </div>

                  {/* Actual Output */}
                  <div className="bg-secondary/50 rounded-lg p-3" data-testid="comparison-dashboard-actual-column">
                    <p className="text-[10px] font-semibold uppercase text-muted-foreground mb-2">Actual Submitted</p>
                    {comp.actual_output ? (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">E/M:</span>
                          <Badge className="font-mono text-xs">{comp.actual_output.primary_code}</Badge>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          <span className="text-xs text-muted-foreground">Dx:</span>
                          {(comp.actual_output.diagnosis_codes || []).map((dx, i) => (
                            <Badge key={i} variant="outline" className="text-[10px] font-mono">{dx}</Badge>
                          ))}
                        </div>
                      </div>
                    ) : comp.status === 'pending_review' ? (
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs">E/M Code</Label>
                          <Input size="sm" value={actualInput.primary_code} onChange={e => setActualInput(p => ({ ...p, primary_code: e.target.value }))}
                            placeholder="e.g., 99214" className="h-8 text-xs mt-1" />
                        </div>
                        <div>
                          <Label className="text-xs">Diagnosis Codes (comma-separated)</Label>
                          <Input size="sm" value={actualInput.diagnosis_codes} onChange={e => setActualInput(p => ({ ...p, diagnosis_codes: e.target.value }))}
                            placeholder="e.g., E11.9, M25.561" className="h-8 text-xs mt-1" />
                        </div>
                        <Button size="sm" className="w-full h-7 text-xs" onClick={() => submitActual(comp.id)}>
                          Submit Actual
                        </Button>
                      </div>
                    ) : <p className="text-xs text-muted-foreground">Waiting for actual billing</p>}
                  </div>
                </div>

                {/* Verdict */}
                {comp.actual_output && !comp.verdict && (
                  <div className="mt-3 pt-3 border-t" data-testid="comparison-dashboard-verdict-radio">
                    <p className="text-xs font-medium mb-2">Record Verdict</p>
                    <div className="flex gap-2 mb-2">
                      {['agree', 'partial', 'disagree'].map(v => (
                        <Button key={v} size="sm" variant={verdictInput.verdict === v ? 'default' : 'outline'}
                          className="h-7 text-xs capitalize" onClick={() => setVerdictInput(p => ({ ...p, verdict: v }))}>
                          {v}
                        </Button>
                      ))}
                    </div>
                    <Input value={verdictInput.notes} onChange={e => setVerdictInput(p => ({ ...p, notes: e.target.value }))}
                      placeholder="Notes (optional)" className="h-8 text-xs mb-2" />
                    <Button size="sm" className="h-7 text-xs gap-1" onClick={() => submitVerdict(comp.id)}
                      disabled={!verdictInput.verdict} data-testid="comparison-dashboard-save-verdict-button">
                      <Check className="w-3 h-3" /> Save Verdict
                    </Button>
                  </div>
                )}

                {comp.reviewer_notes && (
                  <div className="mt-2 pt-2 border-t">
                    <p className="text-xs text-muted-foreground flex items-center gap-1">
                      <MessageSquare className="w-3 h-3" /> {comp.reviewer_notes}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
}
